import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Building an interactive DOM benchmark, preliminary results",
  "description": "",
  "published": "2018-02-27T00:00:00.000Z",
  "redirect_from": ["/blog/building-interactive-dom-benchmark-preliminary-results/swizec/8219"],
  "categories": "Front End, JavaScript, Livecoding, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should subscribe to `}<a parentName="em" {...{
          "href": "https://www.youtube.com/TheSwizec"
        }}>{`My Youtube`}</a>{` channel to catch me live.`}</em></p>
    <lite-youtube {...{
      "videoid": "Ukm3mRWZtCk",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`After that `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/build-list-virtualization/swizec/8167"
      }}>{`chatroom windowing feature`}</a>{` I built a few weeks ago, I've been thinking a lot about how fast native DOM manipulation has gotten.`}</p>
    <p>{`I've been listening to the `}<a parentName="p" {...{
        "href": "https://www.google.com/search?q=dom+is+slow&oq=dom+is+slow&aqs=chrome..69i57j0l5.1249j0j1&sourceid=chrome&ie=UTF-8"
      }}><em parentName="a">{`"DOM is slow"`}</em></a>{` mantra my whole life. But with that feature, I discovered that the DOM is pretty fast, actually.`}</p>
    <p>{`Did you know it's faster and easier to throw out your DOM nodes and render from scratch? That surprised me.`}</p>
    <p>{`So I started working on `}<a parentName="p" {...{
        "href": "https://dom-benchmark.now.sh/"
      }}>{`this DOM manipulation benchmark`}</a>{`. My goal is to give you a live benchmark that you can play with. A way to `}<em parentName="p">{`feel`}</em>{` how fast or slow different approaches are.`}</p>
    <p>{`It's not complete yet. You can already test React using local state, a naive vanilla JS implementation, and a smart vanilla JS implementation. I'm adding Vue, Preact, Angular, and maybe some others.`}</p>
    <p>{`The test focuses on long flat lists. Those are the most common source of having thousands of nodes. Think chatrooms, news feeds, comments, data viz. All long flat lists of nodes.`}</p>
    <p>{`We're testing how fast it is to`}</p>
    <ol>
      <li parentName="ol">{`Prepend 1000 nodes`}</li>
      <li parentName="ol">{`Insert 1000 nodes in the middle`}</li>
      <li parentName="ol">{`Append 1000 nodes`}</li>
      <li parentName="ol">{`Drop all nodes`}</li>
      <li parentName="ol">{`Remove 1 random node`}</li>
    </ol>
    <h2 {...{
      "id": "react-using-local-state"
    }}>{`React using local state`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/02/react-dom.gif",
        "alt": null
      }}></img></p>
    <p>{`React is fast. Between `}<inlineCode parentName="p">{`15ms`}</inlineCode>{` and `}<inlineCode parentName="p">{`21ms`}</inlineCode>{` on average to prepend, insert, and append 1000 nodes. Removing 30,000 nodes is slower because it has to change a lot of individual nodes.`}</p>
    <p>{`The benchmark benefits from nodes being stable. That means a node with the same `}<inlineCode parentName="p">{`key`}</inlineCode>{` never changes. You can see `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/dom-benchmark/blob/master/src/benchmarks/ReactBenchmark.js"
      }}>{`the code on GitHub`}</a>{`.`}</p>
    <p>{`We manipulate a list in `}<inlineCode parentName="p">{`this.state`}</inlineCode>{` and measure times between `}<inlineCode parentName="p">{`componentWillUpdate`}</inlineCode>{` and `}<inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{`. This should exclude the time it takes to manipulate our list.`}</p>
    <p>{`What surprised me is the stark difference between dev-mode React and production-built React. React in dev mode is not only slow, but also gets increasingly slower the more nodes you're rendering. In production mode performance is constant.`}</p>
    <p>{`Neat. 👌`}</p>
    <h2 {...{
      "id": "naive-vanilla-js"
    }}>{`Naive vanilla JS`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/02/naive-js-dom.gif",
        "alt": null
      }}></img></p>
    <p>{`The naive approach is slow. Faster than you'd expect, good for small lists, but not a scalable solution. The more nodes you render, the slower it gets.`}</p>
    <p>{`The core of this benchmark is the render code. Drops all nodes and renders from scratch every time.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    naiveRender() {
        let start = new Date();
        // remove all existing nodes
        // from https://stackoverflow.com/questions/3955229/remove-all-child-elements-of-a-dom-node-in-javascript
        let scratchpad = this.refs.scratchpad;

        while (scratchpad.firstChild) {
            scratchpad.removeChild(scratchpad.firstChild);
        }

        // append all nodes from scratch
        this.nodes.forEach(k => {
            let node = document.createElement("div");
            node.appendChild(document.createTextNode(k));
            scratchpad.appendChild(node);
        });

        let end = new Date();
        this.times.push(end - start);

        // update meta info
        this.refs.time.innerHTML = \`<code>\${end - start}ms</code>\`;
        this.refs.currentCount.innerHTML = this.nodes.length;
        this.refs.avgTime.innerHTML = this.averageTime;
    }
`}</code></pre>
    <p>{`You can see the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/dom-benchmark/blob/master/src/benchmarks/VanillaBenchmark.js"
      }}>{`full benchmark on GitHub`}</a>{`.`}</p>
    <p>{`This renders 1000 nodes in about `}<inlineCode parentName="p">{`4ms`}</inlineCode>{`, which is faster than React. But 4000 takes `}<inlineCode parentName="p">{`32ms`}</inlineCode>{`. Almost three times as much as React.`}</p>
    <p>{`Curiously similar performance curve to dev-mode React. 🤔`}</p>
    <h2 {...{
      "id": "smart-vanilla-js"
    }}>{`Smart vanilla JS`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/02/smart-js-dom.gif",
        "alt": null
      }}></img></p>
    <p>{`A slightly smart vanilla JS approach is `}<em parentName="p">{`blazing fast`}</em>{`. Constant performance around `}<inlineCode parentName="p">{`2ms`}</inlineCode>{`. Wat 🤨`}</p>
    <p>{`I used the new `}<inlineCode parentName="p">{`prepend()`}</inlineCode>{` and `}<inlineCode parentName="p">{`append()`}</inlineCode>{` DOM methods. Nothing super clever, just a basic implementation of what we're testing.`}</p>
    <p>{`You can see the whole `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/dom-benchmark/blob/master/src/benchmarks/SmartVanillaBenchmark.js"
      }}>{`benchmark on GitHub`}</a>{`, but here's the `}<inlineCode parentName="p">{`prepend`}</inlineCode>{` code for example.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`prepend = () => {
  let nodes = this.newNodes,
    scratchpad = this.refs.scratchpad;
  this.nodes = [...nodes, ...this.nodes];

  let start = new Date();

  nodes.map((k) => {
    let node = document.createElement("div");
    node.appendChild(document.createTextNode(k));
    return node;
  });
  scratchpad.prepend(nodes);

  this.updateMeta(start);
};
`}</code></pre>
    <p>{`We're still manipulating the `}<inlineCode parentName="p">{`this.nodes`}</inlineCode>{` array. Keeps "data" and DOM in sync, stays consistent to the other implementations.`}</p>
    <p>{`Then we walk through the list of new nodes, create `}<inlineCode parentName="p">{`div`}</inlineCode>{` elements for each of them, attach some text, and finally `}<inlineCode parentName="p">{`prepend()`}</inlineCode>{` the whole array of nodes into the DOM. This, it turns out, is fast. 🔥`}</p>
    <p>{`1000 nodes in `}<inlineCode parentName="p">{`1ms`}</inlineCode>{` to `}<inlineCode parentName="p">{`2ms`}</inlineCode>{`. A tenth of the time it takes React to do it. Similar results for inserting and appending:`}</p>
    <ul>
      <li parentName="ul">{`appends in about `}<inlineCode parentName="li">{`2ms`}</inlineCode></li>
      <li parentName="ul">{`inserts in about `}<inlineCode parentName="li">{`5ms`}</inlineCode></li>
    </ul>
    <p>{`Inserting is slowest because there's no magic method for it. You have to `}<inlineCode parentName="p">{`insertBefore`}</inlineCode>{` each DOM node individually. The browser then has to shove the entire list around to make room.`}</p>
    <p>{`Even dropping all nodes is crazy fast despite dropping 1-by-1 👉 around `}<inlineCode parentName="p">{`2ms`}</inlineCode>{` for 40,000 nodes. Wow.`}</p>
    <h2 {...{
      "id": "conclusion"
    }}>{`Conclusion`}</h2>
    <p>{`In conclusion, vanilla JavaScript is fast if you know what you're doing. Faster probably than anything else I can add to this benchmark.`}</p>
    <p><strong parentName="p">{`But`}</strong>{` it's not as powerful as React or a similar framework. It takes more time to build, it's hyper optimized for this example, and it wouldn't scale in a real world environment.`}</p>
    <p>{`Code is hard to maintain. You waste time thinking about rendering instead of what you're building, and you will cry as soon as nodes stop being stable. Just imagine figuring out by hand which nodes' contents changed and which didn't.`}</p>
    <p>{`That's what React is doing for you 👉 Spending a little runtime to save a lot of dev time.`}</p>
    <p>{`Can't wait to add Vue and Preact to the mix.`}</p>
    <p><img parentName="p" {...{
        "src": "/85a9584af33665ed8a39644b79a7edd1/media-l2JehIXdp8F7Up5sc-giphy.gif",
        "alt": null
      }}></img></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      